<template>
    <v-row dense>
        <v-col cols="12" md="5">
            <v-text-field
                v-model="search"
                variant="outlined"
                prepend-inner-icon="mdi-magnify"
                label="Search"
                density="comfortable"
                solo
                hide-details
                rounded="lg"
                color="primary">
            </v-text-field>
        </v-col>
        <v-col class="d-flex justify-end" cols="12" md="7">
            <v-btn
                @click="showAddDialog()"
                prepend-icon="mdi-plus"
                class="mt-1 rounded-lg"
                color="routesColor"
                height="40px"
                elevation="2">
                Add Course Outcome
            </v-btn>
        </v-col>

        <!-- Legend -->
        <v-col cols="12">
            <div class="text-caption text-grey-darken-1 mt-1">
                <strong>Legend:</strong>&nbsp;
                <v-chip size="x-small" color="blue" class="mr-1">I</v-chip> Introductory &nbsp;
                <v-chip size="x-small" color="orange" class="mr-1">E</v-chip> Enabling &nbsp;
                <v-chip size="x-small" color="green" class="mr-1">D</v-chip> Demonstrative
            </div>
        </v-col>

        <v-col cols="12">
            <v-card class="dt-container mt-2 rounded-lg" elevation="2">
                <v-data-table
                    :mobile="isMobileOrTablet()"
                    :headers="headers"
                    :items="data"
                    :search="search"
                    :loading="loading"
                    :items-per-page="15"
                    :items-per-page-options="[5, 10, 15, 20, -1]"
                    item-value="id"
                >
                    <template v-slot:loading>
                        <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg"></v-progress-linear>
                    </template>

                    <template v-slot:headers>
                        <tr>
                            <th class="text-start" style="min-width:90px;">CLO Code</th>
                            <th class="text-start" style="min-width:260px;">Description</th>
                            <th
                                v-for="po in programOutcomes"
                                :key="po.id"
                                class="text-center po-col"
                                style="min-width:48px; max-width:56px;"
                            >
                                <v-tooltip :text="po.code + ': ' + po.description" location="top" max-width="220">
                                    <template v-slot:activator="{ props }">
                                        <span v-bind="props" style="cursor: default;">{{ po.code }}</span>
                                    </template>
                                </v-tooltip>
                            </th>
                            <th class="text-end" style="min-width:120px;">Actions</th>
                        </tr>
                    </template>

                    <template v-slot:item="{ item }">
                        <tr>
                            <td class="font-weight-medium">{{ item.code }}</td>
                            <td><div class="w-75 text-truncate">{{ item.description }}</div></td>
                            <td
                                v-for="po in programOutcomes"
                                :key="po.id"
                                class="text-center"
                            >
                                <v-chip
                                    v-if="item.program_outcomes && item.program_outcomes[po.code]"
                                    size="x-small"
                                    :color="getAlignmentColor(item.program_outcomes[po.code])"
                                    variant="elevated"
                                >
                                    {{ item.program_outcomes[po.code] }}
                                </v-chip>
                            </td>
                            <td>
                                <div class="d-flex justify-end ga-2 py-1">
                                    <v-btn
                                        @click="showEditDialog(item)"
                                        size="x-small"
                                        variant="outlined"
                                        text="Edit"
                                        color="orange"
                                        elevation="2"
                                    >
                                    </v-btn>
                                    <v-btn
                                        @click="showDeleteDialog(item)"
                                        size="x-small"
                                        variant="outlined"
                                        text="Remove"
                                        color="red"
                                        elevation="2"
                                    >
                                    </v-btn>
                                </div>
                            </td>
                        </tr>
                    </template>

                    <template v-slot:no-data>
                        <div class="d-flex justify-center pa-8">
                            <div class="text-center text-grey">
                                <v-icon size="48" class="mb-2">mdi-note-alert-outline</v-icon>
                                <div class="text-body-2">No course outcomes found. Click "Add Course Outcome" to get started.</div>
                            </div>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
        </v-col>
    </v-row>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDelete" width="450" persistent>
        <v-card color="white" rounded="lg" class="pa-2">
            <v-card-text>
                <div class="font-weight-bold mt-2 text-h6">Remove this course outcome?</div>
                <div class="font-weight-light mt-2 text-body-2">
                    <strong>{{ itemToDelete ? itemToDelete.code : '' }}</strong>
                    {{ itemToDelete ? ': ' + itemToDelete.description : '' }}
                </div>
                <div class="mt-2 text-caption text-grey">Note: Once confirmed, this action cannot be reversed.</div>
            </v-card-text>
            <template v-slot:actions>
                <v-spacer></v-spacer>
                <v-btn @click="confirmDelete = false" :disabled="loadingConfirmation" variant="outlined">
                    Cancel
                </v-btn>
                <v-btn :loading="loadingConfirmation" @click="deleteOutcome()" color="red" variant="elevated">
                    Confirm
                </v-btn>
            </template>
        </v-card>
    </v-dialog>

    <CourseOutcomesDialog ref="CourseOutcomesDialog" @refresh="initData(course, program)" />
</template>

<script>
import { defineComponent } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'
import CourseOutcomesDialog from '../Dialogs/CourseOutcomesDialog.vue'

export default defineComponent({
    components: {
        CourseOutcomesDialog,
    },
    data() {
        return {
            loading: false,
            search: null,
            data: [],
            programOutcomes: [],
            course: null,
            program: null,
            confirmDelete: false,
            itemToDelete: null,
            loadingConfirmation: false,
        }
    },
    computed: {
        headers() {
            return [
                { title: 'CLO Code', align: 'start', sortable: true, key: 'code' },
                { title: 'Description', align: 'start', sortable: true, key: 'description' },
                ...this.programOutcomes.map((po) => ({
                    title: po.code,
                    align: 'center',
                    sortable: false,
                    key: `po_${po.code}`,
                })),
                { title: 'Actions', align: 'end', sortable: false, key: 'action' },
            ]
        },
    },
    methods: {
        async initData(course, program) {
            this.loading = true
            this.course = course
            this.program = program

            const [poRes, cloRes] = await Promise.all([
                this.axiosCall(
                    '/program-outcomes/findAllByProgramId/' + program.id + '/' + this.$store.state.semester_schoolyear.id,
                    'get', null, true
                ),
                this.axiosCall(
                    '/course-outcomes/findAllByCourseCode/' + course.code + '/' + this.$store.state.semester_schoolyear.id,
                    'get', null, true
                )
            ])

            if (poRes && poRes.data) {
                this.programOutcomes = poRes.data
            }
            if (cloRes && cloRes.data) {
                this.data = cloRes.data
            }
            this.loading = false
        },
        getAlignmentColor(value) {
            if (value === 'I') return 'blue'
            if (value === 'E') return 'orange'
            if (value === 'D') return 'green'
            return 'grey'
        },
        showAddDialog() {
            this.$refs.CourseOutcomesDialog.initAdd(this.course, this.program)
        },
        showEditDialog(item) {
            this.$refs.CourseOutcomesDialog.initEdit(item, this.course, this.program)
        },
        showDeleteDialog(item) {
            this.itemToDelete = item
            this.confirmDelete = true
        },
        async deleteOutcome() {
            const { showSnackbar } = useSnackbar()
            this.loadingConfirmation = true
            let res = await this.axiosCall('/course-outcomes/' + this.itemToDelete.id, 'delete', null, true)
            if (res && res.data && res.data.status === 200) {
                this.loadingConfirmation = false
                showSnackbar('Course outcome successfully removed!')
                this.confirmDelete = false
                this.initData(this.course, this.program)
            } else {
                this.loadingConfirmation = false
            }
        },
    }
})
</script>

<style scoped>
.po-col {
    font-size: 0.78rem;
}

.description-cell {
    max-width: 320px;
}
</style>
